import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Check, X, EyeOff, Bot, ShieldCheck, Phone, MapPin, Camera, AlertTriangle } from "lucide-react";

const REASON_LABELS = {
  blocked: "Blocked parking",
  emergency: "Emergency",
  lights: "Lights left on",
  window: "Window open",
  tyre: "Flat tyre",
  scratch: "Scratch / damage",
};

export default function AlertDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [alert, setAlert] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/alerts/${id}`);
      setAlert(data);
    } catch (err) {
      toast.error(formatApiError(err));
      nav("/dashboard");
    }
  };
  useEffect(() => { load(); }, [id]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/alerts/${id}/decision`, { decision });
      setAlert(data);
      toast.success(`Marked as ${decision.replace("_", " ")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!alert) return <DashLayout title="Alert"><div className="text-gray-500">Loading…</div></DashLayout>;

  const conf = Math.round((alert.ai_confidence || 0) * 100);
  const isUrgent = alert.reason === "emergency" || alert.reason === "blocked";
  const decided = !!alert.owner_decision;

  return (
    <DashLayout title={REASON_LABELS[alert.reason]}>
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 mb-8 hover:text-[#002FA7]" data-testid="alert-back">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Main */}
        <div className="lg:col-span-8 space-y-6">
          <div className={`card-flat overflow-hidden ${isUrgent && !decided ? "border-[#E63946]" : ""}`} data-testid="alert-header">
            <div className={`p-5 border-b ${isUrgent ? "bg-[#E63946] text-white border-[#E63946]" : "bg-[#F9F9F8] border-gray-200"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-eyebrow opacity-80">{isUrgent ? "Urgent alert" : "Alert"}</div>
                  <div className="font-display font-black text-3xl mt-1">{REASON_LABELS[alert.reason]}</div>
                  <div className="font-mono text-xs mt-2 opacity-80">{alert.reference}</div>
                </div>
                <AlertTriangle size={32} className={isUrgent ? "text-white" : "text-gray-400"} />
              </div>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4 text-sm">
              <div><div className="label-eyebrow mb-1">Status</div><div className="font-bold capitalize">{alert.status}</div></div>
              <div><div className="label-eyebrow mb-1">Reported at</div><div className="font-mono">{new Date(alert.created_at).toLocaleString()}</div></div>
              {alert.note && <div className="col-span-2"><div className="label-eyebrow mb-1">Scanner's note</div><div className="text-gray-700">{alert.note}</div></div>}
            </div>
          </div>

          {/* AI verdict */}
          <div className="card-flat p-6" data-testid="ai-verdict">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Bot size={20} className="text-[#002FA7]" />
                <div className="label-eyebrow">AI verification</div>
              </div>
              <div className="font-display font-black text-3xl tabular text-[#002FA7]">{conf}<span className="text-base text-gray-400">%</span></div>
            </div>
            <p className="text-gray-700 leading-relaxed">{alert.ai_analysis?.ai_summary}</p>
            <div className="seg-bar mt-4">
              {Array.from({ length: 20 }).map((_, i) => (
                <span key={i} className={i < Math.round(conf / 5) ? "on" : ""} />
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 text-xs font-mono">
              <Pill label={`recommend: ${alert.ai_analysis?.recommendation}`} />
              {alert.ai_analysis?.blocking_detected && <Pill label="blocking detected" tone="warning" />}
              {alert.ai_analysis?.anomalies?.length > 0 && <Pill label={`anomalies: ${alert.ai_analysis.anomalies.join(",")}`} tone="danger" />}
            </div>
          </div>

          {/* Scene photo */}
          {alert.scene_photo_url && (
            <div className="card-flat overflow-hidden" data-testid="scene-photo">
              <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                <Camera size={16} /> <div className="label-eyebrow">Scene photo</div>
              </div>
              <img src={alert.scene_photo_url} alt="scene" className="w-full max-h-[500px] object-contain bg-black" />
            </div>
          )}

          {/* Actions */}
          {!decided && (
            <div className="card-flat p-5" data-testid="decision-buttons">
              <div className="label-eyebrow mb-4">Your decision</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => decide("confirmed")} disabled={busy} className="btn btn-success flex-1" data-testid="confirm-button">
                  <Check size={16} /> Yes, I'm on it
                </button>
                <button onClick={() => decide("false_report")} disabled={busy} className="btn btn-danger flex-1" data-testid="false-report-button">
                  <X size={16} /> False report
                </button>
                <button onClick={() => decide("ignored")} disabled={busy} className="btn btn-ghost flex-1" data-testid="ignore-button">
                  <EyeOff size={16} /> Ignore
                </button>
              </div>
            </div>
          )}
          {decided && (
            <div className="card-flat p-5 bg-[#F9F9F8]" data-testid="decision-status">
              <div className="label-eyebrow mb-2">Decided</div>
              <div className="font-display font-bold text-xl capitalize">{alert.owner_decision.replace("_", " ")}</div>
              {alert.owner_decided_at && <div className="text-xs font-mono text-gray-500 mt-1">{new Date(alert.owner_decided_at).toLocaleString()}</div>}
            </div>
          )}
        </div>

        {/* Sidebar: scanner identity */}
        <aside className="lg:col-span-4 space-y-4">
          {alert.scanner_identity ? (
            <div className="card-flat p-5" data-testid="scanner-identity">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow">Scanner identity</div>
                <span className="inline-flex items-center gap-1 text-xs font-mono text-[#10B981]"><ShieldCheck size={12} /> verified</span>
              </div>
              <div className="space-y-3 text-sm">
                <Field label="Name" value={alert.scanner_identity.name} />
                <Field label="Phone" value={alert.scanner_identity.phone} mono icon={<Phone size={12} />} />
                <Field label="Plate" value={alert.scanner_identity.plate_number} mono />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {alert.scanner_identity.selfie_url && (
                  <div className="border border-gray-200 overflow-hidden">
                    <div className="label-eyebrow p-2 text-[10px]">Selfie</div>
                    <img src={alert.scanner_identity.selfie_url} alt="selfie" className="w-full aspect-square object-cover" data-testid="scanner-selfie" />
                  </div>
                )}
                {alert.scanner_identity.car_photo_url && (
                  <div className="border border-gray-200 overflow-hidden">
                    <div className="label-eyebrow p-2 text-[10px]">Their car</div>
                    <img src={alert.scanner_identity.car_photo_url} alt="scanner car" className="w-full aspect-square object-cover" data-testid="scanner-car" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card-flat p-5" data-testid="anonymous-scanner">
              <div className="label-eyebrow mb-2">Anonymous scanner</div>
              <div className="text-sm text-gray-600">This reason ({REASON_LABELS[alert.reason]}) doesn't require identity. Treat with discretion.</div>
            </div>
          )}

          {alert.scene_gps_lat && alert.scene_gps_lng && (
            <div className="card-flat p-5">
              <div className="label-eyebrow mb-2 flex items-center gap-2"><MapPin size={12} /> Location</div>
              <div className="font-mono text-xs text-gray-700">
                {alert.scene_gps_lat.toFixed(5)}, {alert.scene_gps_lng.toFixed(5)}
              </div>
            </div>
          )}
        </aside>
      </div>
    </DashLayout>
  );
}

function Pill({ label, tone }) {
  const color = tone === "danger" ? "bg-[#E63946] text-white" : tone === "warning" ? "bg-[#F59E0B] text-white" : "bg-gray-100 text-gray-700";
  return <span className={`px-2 py-1 ${color}`}>{label}</span>;
}

function Field({ label, value, mono, icon }) {
  return (
    <div>
      <div className="label-eyebrow text-[10px] mb-0.5">{label}</div>
      <div className={`flex items-center gap-2 ${mono ? "font-mono" : "font-medium"}`}>
        {icon}
        {value}
      </div>
    </div>
  );
}
